/**
 * Landing page: LP Cluster
 * -----------------------------------------------------------------------------
 * Guarantee tooltip in the hero. The toggle button opens and closes it, a
 * click anywhere else or Escape closes it. Listeners are delegated so a
 * section reloaded in the theme editor keeps working without binding twice.
 *
 */
(() => {
  const TOGGLE = '[js-tooltip="toggle"]'
  const OPEN_CLASS = 'lp-cluster-hero__guarantee--open'
  const DISMISSED_CLASS = 'lp-cluster-hero__guarantee--dismissed'

  /**
   * Open or close the tooltip that belongs to a toggle button.
   * @param {HTMLElement} toggle - Tooltip toggle button.
   * @param {Boolean} isOpen - Whether the tooltip should be open.
   */
  const setOpen = (toggle, isOpen) => {
    const wrapper = toggle.parentElement

    wrapper.classList.toggle(OPEN_CLASS, isOpen)
    wrapper.classList.remove(DISMISSED_CLASS)
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
  }

  /**
   * Close every open tooltip.
   * @param {HTMLElement} [except] - Toggle to leave as it is.
   */
  const closeAll = (except) => {
    document.querySelectorAll(`${TOGGLE}[aria-expanded="true"]`).forEach((toggle) => {
      if (toggle === except) {
        return
      }

      setOpen(toggle, false)
    })
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest(TOGGLE)

    closeAll(toggle)

    if (!toggle) {
      return
    }

    setOpen(toggle, toggle.getAttribute('aria-expanded') !== 'true')
  })

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') {
      return
    }

    closeAll()

    // Also hide a tooltip shown by hover or keyboard focus
    document.querySelectorAll(TOGGLE).forEach((toggle) => {
      toggle.parentElement.classList.add(DISMISSED_CLASS)
    })
  })

  /**
   * Let a dismissed tooltip show again once pointer and focus have left.
   * @param {Event} event - Pointerleave or focusout event.
   */
  const resetDismissed = (event) => {
    const wrapper = event.target.closest?.(`.${DISMISSED_CLASS}`)

    if (!wrapper || wrapper.matches(':hover, :focus-within')) {
      return
    }

    wrapper.classList.remove(DISMISSED_CLASS)
  }

  document.addEventListener('pointerout', resetDismissed)
  document.addEventListener('focusout', (event) => {
    // The new focus target is only known after the event
    requestAnimationFrame(() => resetDismissed(event))
  })
})()
